import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCommonFiles } from './apis'
import { CommonFileItemRes } from './types'

/**
 * 행별 첨부·동영상 목록 — 계획서 4.5. 조회가 끝나면 응답 행들의 `fileId` 를 모아
 * `GET /api/v1/common/files` 를 한 번 부른다(3.7 — 셀 값은 개수만 필요하지만, 모달에서
 * key·파일명까지 써야 해서 전체 항목을 들고 있는다).
 */
export const useRowMediaFiles = (shipper: string, fileIds: string[], shipperGroups?: { shipper: string; fileIds: string[] }[]) => {
  const [filesByFileId, setFilesByFileId] = useState<Map<string, CommonFileItemRes>>(new Map())

  // 배열 레퍼런스가 아니라 내용으로 의존한다 — 호출부가 매 렌더 새 배열을 넘겨도 불필요한 재조회를 막는다.
  const uniqueIds = useMemo(() => [...new Set(fileIds.filter(Boolean))].sort((a, b) => a.localeCompare(b)), [fileIds])
  const key = useMemo(() => JSON.stringify(shipperGroups ?? [{ shipper, fileIds: uniqueIds }]), [uniqueIds, shipper, shipperGroups])

  // `uniqueIds` 를 deps 에 넣지 않는다 — 배열은 매번 새 레퍼런스라(useMemo 는 fileIds 레퍼런스가
  // 바뀌면 항상 재계산한다), 넣으면 호출부가 fileIds 를 메모이즈하지 않을 때마다 `fetchAll` 이
  // 매 렌더 재생성되고 아래 `useEffect` 가 무한 재실행된다(리뷰 반영 시도 중 실측 — 테스트에서
  // OOM 으로 드러났다). `key`(문자열, 값 동일성)만 봐야 "내용이 같으면 재조회 안 함"이 성립한다.
  const fetchAll = useCallback(async () => {
    if (!shipper || uniqueIds.length === 0) {
      setFilesByFileId(new Map())
      return
    }
    try {
      const responses = await Promise.all((shipperGroups ?? [{ shipper, fileIds: uniqueIds }]).map((group) => getCommonFiles(group.shipper, group.fileIds)))
      setFilesByFileId(new Map(responses.flatMap((res) => res.data.items.map((item) => [item.fileId, item] as const))))
    } catch {
      // 개수 표시 실패는 사용자 작업을 막지 않는다 — 조용히 흘린다(다음 조회·재시도로 복구된다).
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipper, key])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  /** 행 첨부·동영상 개수(atchFileCount·videoCount) — 셀 값으로 그대로 쓴다(3.7). */
  const counts = useMemo(() => {
    const map = new Map<string, { atchFileCount: number; videoCount: number }>()
    filesByFileId.forEach((item, fileId) => map.set(fileId, { atchFileCount: item.attachments.length, videoCount: item.videos.length }))
    return map
  }, [filesByFileId])

  return { filesByFileId, counts, refetch: fetchAll }
}
